"use client";

/**
 * Metric measurability assessment checklist.
 *
 * Extracted from `compass-intakeflow.jsx:236-250`.
 */

import { Bulb, Check } from "../lib/icons";

export interface MeasureMetric {
  name: string;
}

export interface MeasureState {
  unit?: string;
  measurable?: "yes" | "no";
  baseline?: string;
}

export interface IntakeMeasureProps {
  metrics: MeasureMetric[];
  measurements?: Record<number, MeasureState>;
  onChange?: (index: number, state: MeasureState) => void;
}

export function IntakeMeasure({
  metrics,
  measurements = {},
  onChange,
}: IntakeMeasureProps) {
  return (
    <div className="flex flex-col gap-3">
      {metrics.map((m, i) => {
        const st = measurements[i];
        return (
          <div
            key={i}
            className="border border-(--border-subtle) rounded-(--radius-field) p-3 bg-(--bg-white)"
          >
            <span className="flex items-center gap-2">
              <span className="text-[13.5px] font-medium text-(--fg-primary)">
                {m.name}
              </span>
            </span>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                className={[
                  "flex items-center gap-1.5 text-[12.5px] font-medium",
                  "border rounded-(--radius-field) px-3 py-2 cursor-pointer",
                  "font-[family-name:var(--font-body)]",
                  st?.measurable === "yes"
                    ? "border-(--fg-brand) bg-(--bg-sky) text-(--fg-brand)"
                    : "border-(--border-default) text-(--fg-secondary) bg-(--bg-white) hover:border-(--border-strong)",
                ].join(" ")}
                onClick={() => onChange?.(i, { ...st, measurable: "yes" })}
              >
                {st?.measurable === "yes" && <Check size={14} />}Measured today
              </button>
              <button
                type="button"
                className={[
                  "flex items-center gap-1.5 text-[12.5px] font-medium",
                  "border rounded-(--radius-field) px-3 py-2 cursor-pointer",
                  "font-[family-name:var(--font-body)]",
                  st?.measurable === "no"
                    ? "border-(--fg-brand) bg-(--bg-sky) text-(--fg-brand)"
                    : "border-(--border-default) text-(--fg-secondary) bg-(--bg-white) hover:border-(--border-strong)",
                ].join(" ")}
                onClick={() => onChange?.(i, { ...st, measurable: "no" })}
              >
                {st?.measurable === "no" && <Check size={14} />}Not yet
              </button>
            </div>
            {st?.measurable === "yes" && (
              <input
                type="text"
                className="mt-2 w-full text-[13px] text-(--fg-primary) bg-(--bg-white) border border-(--border-default) rounded-(--radius-sm) px-[10px] py-[8px] outline-none focus:border-(--fg-brand) font-[family-name:var(--font-body)]"
                placeholder="Current baseline"
                value={st.baseline ?? ""}
                onChange={(e) =>
                  onChange?.(i, { ...st, baseline: e.target.value })
                }
              />
            )}
            {st?.measurable === "no" && (
              <div className="flex items-center gap-2 mt-2 text-[12px] text-(--fg-muted)">
                <Bulb size={13} /> We'll estimate a baseline for you.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
